import React, { useState, useEffect } from 'react';
import { X, Save, BookOpen, Users, Award, Settings as SettingsIcon, Layout } from 'lucide-react';
import { courseService } from '../../services/courseService';
import { BasicInfoTab } from '../settings/shared/BasicInfoTab';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { PrivacySection } from '../settings/shared/PrivacySection';
import { SEOSection } from '../settings/shared/SEOSection';
import { IntegrationsSection } from '../settings/shared/IntegrationsSection';
import { InteractionsSection } from '../settings/shared/InteractionsSection';
import { PaymentsSection } from '../settings/shared/PaymentsSection';
import { FileUpload } from '../common/FileUpload';
import { firestoreService } from '../../services/firestoreService';


export const CourseSettings = ({ bookId, bookData, onUpdate, onClose }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    // Basic Info
    botName: '',
    description: '',
    courseType: 'self-paced',
    level: 'beginner',
    language: 'en',
    category: '',
    tags: [],
    
    // Course Structure
    lessonStructure: 'linear',
    enableModules: true,
    enablePreview: true,
    previewLessons: [],
    
    // Access Control
    enrollmentType: 'open',
    isPremium: false,
    price: 0,
    currency: 'USD',
    maxEnrollments: null,
    
    // Display Settings
    showProgress: true,
    showCertificate: true,
    allowDownloads: false,
    enableDiscussions: true,
    enableNotes: true,
    showInstructor: true,
    
    // Instructor Info
    instructorInfo: {
      name: '',
      bio: '',
      imageUrl: '',
      credentials: []
    },
    
    // Completion Settings
    completionCriteria: {
      requireAllLessons: true,
      minimumScore: 80,
      requireAssessments: true,
      issuesCertificate: false
    },
    
    // Design
    primaryColor: '#3b82f6',
    backgroundColor: '#ffffff',
    headerSettings: {},
    
    // Privacy
    visibility: 'public',
    requireLogin: false,
    
    // SEO
    customDomain: '',
    customPath: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    favicon: '',
    ogImage: '',
    
    // Integrations
    enableAnalytics: false,
    
    // Interactions
    enableComments: true,
    enableRatings: true,
    enableQA: true
  });
  
  const [saving, setSaving] = useState(false);
  const [uploadStates, setUploadStates] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
  
    const [logoFile, setLogoFile] = useState(null);
      const [coverImageFile, setCoverImageFile] = useState(null);
      const [headerBackgroundFile, setHeaderBackgroundFile] = useState(null);
      const [faviconFile, setFaviconFile] = useState(null); // ADD THIS LINE
      const [ogImageFile, setOgImageFile] = useState(null); 
      
      // Preview states
      const [logoPreview, setLogoPreview] = useState('');
      const [coverImagePreview, setCoverImagePreview] = useState('');
      const [headerBackgroundPreview, setHeaderBackgroundPreview] = useState('');
      const [faviconPreview, setFaviconPreview] = useState(''); // ADD THIS LINE
      const [ogImagePreview, setOgImagePreview] = useState('');

  useEffect(() => {
    if (bookData) {
      setFormData({
        // Basic Info
        botName: bookData.botName || '',
        description: bookData.description || '',
        courseType: bookData.courseSettings?.courseType || bookData.courseType || 'self-paced',
        level: bookData.courseSettings?.level || bookData.level || 'beginner',
        language: bookData.courseSettings?.language || bookData.language || 'en',
        category: bookData.courseSettings?.category || bookData.category || '',
        tags: bookData.courseSettings?.tags || bookData.tags || [],

        logoUrl: bookData.logoUrl || '',
        favicon: bookData.favicon || bookData.favicon || '',
        ogImage: bookData.ogImage || '',
        
        // Course Structure
        lessonStructure: bookData.courseSettings?.lessonStructure || 'linear',
        enableModules: bookData.courseSettings?.enableModules ?? true,
        enablePreview: bookData.courseSettings?.enablePreview ?? true,
        previewLessons: bookData.courseSettings?.previewLessons || [],
        
        // Access Control
        enrollmentType: bookData.courseSettings?.enrollmentType || 'open',
        isPremium: bookData.courseSettings?.isPremium ?? false,
        price: bookData.courseSettings?.price ?? 0,
        currency: bookData.courseSettings?.currency || 'USD',
        maxEnrollments: bookData.courseSettings?.maxEnrollments || null,
        
        // Display Settings
        showProgress: bookData.courseSettings?.showProgress ?? true,
        showCertificate: bookData.courseSettings?.showCertificate ?? true,
        allowDownloads: bookData.courseSettings?.allowDownloads ?? false,
        enableDiscussions: bookData.courseSettings?.enableDiscussions ?? true,
        enableNotes: bookData.courseSettings?.enableNotes ?? true,
        showInstructor: bookData.courseSettings?.showInstructor ?? true,
        
        // Instructor Info
        instructorInfo: {
          name: bookData.courseSettings?.instructorInfo?.name || '',
          bio: bookData.courseSettings?.instructorInfo?.bio || '',
          imageUrl: bookData.courseSettings?.instructorInfo?.imageUrl || '',
          credentials: bookData.courseSettings?.instructorInfo?.credentials || []
        },
        
        // Completion Settings
        completionCriteria: {
          requireAllLessons: bookData.courseSettings?.completionCriteria?.requireAllLessons ?? true,
          minimumScore: bookData.courseSettings?.completionCriteria?.minimumScore ?? 80,
          requireAssessments: bookData.courseSettings?.completionCriteria?.requireAssessments ?? true,
          issuesCertificate: bookData.courseSettings?.completionCriteria?.issuesCertificate ?? false
        },
        
        // Design
        primaryColor: bookData.styling?.primaryColor || bookData.primaryColor || '#3b82f6',
        accentColor: bookData.styling?.accentColor || bookData.accentColor || '#8b5cf6',
        backgroundColor: bookData.styling?.backgroundColor || bookData.backgroundColor || '#ffffff',
        textColor: bookData.styling?.textColor || bookData.textData || '#1f2937',
        headerBackground: bookData.styling?.headerBackground || bookData.headerBackground || '#ffffff',
        headerColor: bookData.styling?.headerColor || bookData.headerColor || '#1f2937',
        
        headerSettings: bookData.courseSettings?.headerSettings || bookData.headerSettings || {},
        // PRIVACY FIELDS - CRITICAL
        accessLevel: bookData.accessLevel || 'public',
        accessPassword: bookData.accessPassword || '',
        requireAuth: bookData.requireAuth ?? false,
        authorizedUsers: bookData.authorizedUsers || [],
        
        // Management Access
        managementAccess: {
          allOrgUsers: bookData.managementAccess?.allOrgUsers ?? false,
          specificEmails: bookData.managementAccess?.specificEmails || []
        },
        
        // View Access
        viewAccess: {
          enablePublicView: bookData.viewAccess?.enablePublicView ?? true,
          enableManagementView: bookData.viewAccess?.enableManagementView ?? true,
          allowViewToggle: bookData.viewAccess?.allowViewToggle ?? true,
          defaultView: bookData.viewAccess?.defaultView || 'public',
          requireAuthForManagement: bookData.viewAccess?.requireAuthForManagement ?? true
        },
        
        // Additional Privacy Settings
        allowAnonymousViews: bookData.allowAnonymousViews ?? true,
        requireLoginForContact: bookData.requireLoginForContact ?? false,
        moderateSubmissions: bookData.moderateSubmissions ?? false,
        published: bookData.published ?? true,
        organizationId: bookData.organizationId || null,
        
        // Visibility (legacy support)
        visibility: bookData.visibility || 'public',
        requireLogin: bookData.requireLogin ?? false,
        
        // SEO
        customDomain: bookData.customDomain || '',
        customPath: bookData.customPath || '',
        seoTitle: bookData.seoTitle || '',
        seoDescription: bookData.seoDescription || '',
        seoKeywords: bookData.seoKeywords || '',
        
        // Integrations
        enableAnalytics: bookData.enableAnalytics ?? false,
        
        // Interactions
        enableComments: bookData.enableComments ?? true,
        enableRatings: bookData.enableRatings ?? true,
        enableQA: bookData.courseSettings?.enableQA ?? true
      });
    }
  }, [bookData]);

  const initializeAuthorizedUsers = (authorizedUsers) => {
    // If no data, return empty object
    if (!authorizedUsers) return {};
    
    // If already in correct format (object), return as is
    if (!Array.isArray(authorizedUsers) && typeof authorizedUsers === 'object') {
      return authorizedUsers;
    }
    
    // If old format (array), migrate to new format
    if (Array.isArray(authorizedUsers)) {
      return authorizedUsers.reduce((acc, email) => {
        acc[email] = {
          email,
          permissions: {
            management: true,
            voiceRecording: true,
            uploads: true
          },
          addedAt: new Date().toISOString()
        };
        return acc;
      }, {});
    }
    
    return {};
  };

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      if (path.includes('.')) {
        const keys = path.split('.');
        const newData = { ...prev };
        let current = newData;
        
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          current = current[key];
        }
        
        current[keys[keys.length - 1]] = value;
        return newData;
      }
      
      return { ...prev, [path]: value };
    });
  };

  const handleFileUpload = async (file, fileType) => {
    if (!file) return;

    const actualBookId = bookId || bookData?.id;
    if (!actualBookId) {
      console.error('No book ID available for upload');
      return;
    }

    setUploadStates(prev => ({ ...prev, [fileType]: { uploading: true, error: null } }));

    try {
      const result = await firestoreService.uploadFile(file, `pb-${actualBookId}/courses/${fileType}`);
      
      const fieldMap = {
        'logo': 'logoUrl',
        'favicon': 'favicon',
        'ogImage': 'ogImage'
      };

      setFormData(prev => ({
        ...prev,
        [fieldMap[fileType] || fileType]: result.url
      }));

      setUploadStates(prev => ({ ...prev, [fileType]: { uploading: false, error: null } }));
      
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      setUploadStates(prev => ({ 
        ...prev, 
        [fileType]: { uploading: false, error: error.message } 
      }));
    }
  };

  const handleLogoUpload = (file, error) => {
    if (error) {
      setUploadStates(prev => ({ ...prev, logo: { uploading: false, error } }));
      return;
    }
    handleFileUpload(file, 'logo');
  };

  const handleFaviconUpload = (file, error) => {
    if (error) {
      setUploadStates(prev => ({ ...prev, favicon: { uploading: false, error } }));
      return;
    }
    handleFileUpload(file, 'favicon');
  };

  const handleOgImageUpload = (file, error) => {
    if (error) {
      setUploadStates(prev => ({ ...prev, ogImage: { uploading: false, error } }));
      return;
    }
    handleFileUpload(file, 'ogImage');
  };

  const handleLogoSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
          setLogoFile(file);
          const reader = new FileReader();
          reader.onload = (e) => setLogoPreview(e.target.result);
          reader.readAsDataURL(file);
          setHasChanges(true);
        }
      };
  
      const handleCoverImageSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
          setCoverImageFile(file);
          const reader = new FileReader();
          reader.onload = (e) => setCoverImagePreview(e.target.result);
          reader.readAsDataURL(file);
          setHasChanges(true);
        }
      };
  
      const handleHeaderBackgroundSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
          setHeaderBackgroundFile(file);
          const reader = new FileReader();
          reader.onload = (e) => setHeaderBackgroundPreview(e.target.result);
          reader.readAsDataURL(file);
          setHasChanges(true);
        }
      };
  
      const handleFaviconSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
          setFaviconFile(file);
          const reader = new FileReader();
          reader.onload = (e) => setFaviconPreview(e.target.result);
          reader.readAsDataURL(file);
          setHasChanges(true);
        }
      };
  
      const handleOgImageSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
          setOgImageFile(file);
          const reader = new FileReader();
          reader.onload = (e) => setOgImagePreview(e.target.result);
          reader.readAsDataURL(file);
          setHasChanges(true);
        }
      };
        
    const uploadFiles = async () => {
      setUploading(true);
      setUploadProgress(0);
      
      try {
        const uploads = [];
        const uploadedUrls = {};
        
        if (logoFile) {
          uploads.push({
            file: logoFile,
            path: 'logo',
            field: 'logoUrl'
          });
        }
        
        if (coverImageFile) {
          uploads.push({
            file: coverImageFile,
            path: 'cover',
            field: 'coverImageUrl'
          });
        }
        
        if (headerBackgroundFile) {
          uploads.push({
            file: headerBackgroundFile,
            path: 'header-bg',
            field: 'headerBackgroundUrl'
          });
        }
  
        if (ogImageFile) {
          uploads.push({
            file: ogImageFile,
            path: 'og-image',
            field: 'ogImage'
          });
        }
  
        if (faviconFile) {
          uploads.push({
            file: faviconFile,
            path: 'favicon',
            field: 'favicon'
          });
        }
  
        if (uploads.length === 0) return {};
  
        for (let i = 0; i < uploads.length; i++) {
          const upload = uploads[i];
          const result = await firestoreService.uploadFile(upload.file, `pb-${bookId}/flipbook/${upload.path}`);
          
          uploadedUrls[upload.field] = result.url;
          
          setFormData(prev => ({
            ...prev,
            [upload.field]: result.url
          }));
          
          setUploadProgress(Math.round(((i + 1) / uploads.length) * 100));
        }
  
        return uploadedUrls;
        
      } catch (error) {
        console.error('Error uploading files:', error);
        throw error;
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    };

  const handleSave = async () => {
    if (!bookData) return;
    
    setSaving(true);
    try {
      const updatedBook = {
        ...bookData,
        
        // Basic info at top level
        botName: formData.botName,
        description: formData.description,
        logoUrl: formData.logoUrl,
        favicon: formData.favicon,
        ogImage: formData.ogImage,
        
        // PRIVACY & ACCESS - TOP LEVEL (CRITICAL)
        accessLevel: formData.accessLevel || 'public',
        accessPassword: formData.accessPassword || '',
        requireAuth: formData.requireAuth ?? false,
        authorizedUsers: formData.authorizedUsers || [],
        
        // Management Access
        managementAccess: {
          allOrgUsers: formData.managementAccess?.allOrgUsers ?? false,
          specificEmails: formData.managementAccess?.specificEmails || []
        },
        
        // View Access
        viewAccess: {
          enablePublicView: formData.viewAccess?.enablePublicView ?? true,
          enableManagementView: formData.viewAccess?.enableManagementView ?? true,
          allowViewToggle: formData.viewAccess?.allowViewToggle ?? true,
          defaultView: formData.viewAccess?.defaultView || 'public',
          requireAuthForManagement: formData.viewAccess?.requireAuthForManagement ?? true
        },
        
        // Additional Privacy Settings
        allowAnonymousViews: formData.allowAnonymousViews ?? true,
        requireLoginForContact: formData.requireLoginForContact ?? false,
        moderateSubmissions: formData.moderateSubmissions ?? false,
        published: formData.published ?? true,
        organizationId: formData.organizationId || null,
        
        // Legacy privacy fields (for backward compatibility)
        visibility: formData.visibility || formData.accessLevel || 'public',
        requireLogin: formData.requireLogin ?? formData.requireAuth ?? false,
        
        // SEO at top level
        seoTitle: formData.seoTitle || '',
        seoDescription: formData.seoDescription || '',
        
        // Interactions at top level
        enableComments: formData.enableComments ?? true,
        enableRatings: formData.enableRatings ?? true,
        enableAnalytics: formData.enableAnalytics ?? false,
        
        // Custom styling object
        styling: {
          primaryColor: formData.primaryColor,
          accentColor: formData.accentColor,
          backgroundColor: formData.backgroundColor,
          textColor: formData.textColor,
          headerBackground: formData.headerBackground,
          headerColor: formData.headerColor,
        },

        seoTitle: formData.seoTitle,
        seoDescription: formData.seoDescription,
        seoKeywords: formData.seoKeywords,
          // SEO & Domain
        customDomain: formData.customDomain,
        customPath: formData.customPath,
        
        // All course-specific settings
        courseSettings: {
          // Basic course info
          courseType: formData.courseType,
          level: formData.level,
          language: formData.language,
          category: formData.category,
          tags: formData.tags,
          
          // Course structure
          lessonStructure: formData.lessonStructure,
          enableModules: formData.enableModules,
          enablePreview: formData.enablePreview,
          previewLessons: formData.previewLessons,
          
          // Access control
          enrollmentType: formData.enrollmentType,
          isPremium: formData.isPremium,
          price: formData.price,
          currency: formData.currency,
          maxEnrollments: formData.maxEnrollments,
          
          // Display settings
          showProgress: formData.showProgress,
          showCertificate: formData.showCertificate,
          allowDownloads: formData.allowDownloads,
          enableDiscussions: formData.enableDiscussions,
          enableNotes: formData.enableNotes,
          showInstructor: formData.showInstructor,
          
          // Instructor info
          instructorInfo: {
            name: formData.instructorInfo?.name || '',
            bio: formData.instructorInfo?.bio || '',
            imageUrl: formData.instructorInfo?.imageUrl || '',
            credentials: formData.instructorInfo?.credentials || []
          },
          
          // Completion criteria
          completionCriteria: {
            requireAllLessons: formData.completionCriteria?.requireAllLessons ?? true,
            minimumScore: formData.completionCriteria?.minimumScore ?? 80,
            requireAssessments: formData.completionCriteria?.requireAssessments ?? true,
            issuesCertificate: formData.completionCriteria?.issuesCertificate ?? false
          },
          // Header settings
          headerSettings: formData.headerSettings || {},
          
          // Q&A settings
          enableQA: formData.enableQA
        },
        
        updatedAt: new Date()
      };

      await onUpdate(updatedBook);
      onClose();
    } catch (error) {
      console.error('Error saving course settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: BookOpen },
    { id: 'structure', label: 'Course Structure', icon: Layout },
    { id: 'instructor', label: 'Instructor', icon: Users },
    { id: 'completion', label: 'Completion', icon: Award },
    { id: 'design', label: 'Design', icon: SettingsIcon },
    { id: 'privacy', label: 'Privacy', icon: SettingsIcon },
    { id: 'seo', label: 'SEO', icon: SettingsIcon },
    { id: 'integrations', label: 'Integrations', icon: SettingsIcon },
    { id: 'interactions', label: 'Interactions', icon: SettingsIcon },
    { id: 'payments', label: 'Payments', icon: SettingsIcon }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Course Settings</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-6 py-4 border-b overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'basic' && (
            <CourseBasicInfo
              formData={formData}
              onInputChange={handleInputChange}
              onLogoUpload={handleLogoUpload}
              uploadStates={uploadStates}
            />
          )}
          
          {activeTab === 'structure' && (
            <CourseStructure
              formData={formData}
              onInputChange={handleInputChange}
            />
          )}
          
          {activeTab === 'instructor' && (
            <InstructorSettings
              formData={formData}
              onInputChange={handleInputChange}
            />
          )}
          
          {activeTab === 'completion' && (
            <CompletionSettings
              formData={formData}
              onInputChange={handleInputChange}
            />
          )}
          
          {activeTab === 'design' && (
            <DesignBrandingSection
              formData={formData}
              onInputChange={handleInputChange}
              templateType="course"
            />
          )}
          
          {activeTab === 'privacy' && (
            <PrivacySection
              formData={formData}
              onInputChange={handleInputChange}
            />
          )}
          
          {activeTab === 'seo' && (
            <SEOSection
                        formData={formData}
                        onInputChange={handleInputChange}
                        onImageUpload={(file, fileType) => {
                          // Route to appropriate handler based on fileType
                          if (fileType === 'favicon') {
                            const event = { target: { files: [file] } };
                            handleFaviconSelect(event);
                          } else if (fileType === 'ogImage') {
                            const event = { target: { files: [file] } };
                            handleOgImageSelect(event);
                          }
                        }}
                        uploadStates={uploadStates}
                        bookId={bookId}
                        faviconPreview={faviconPreview}
                        ogImagePreview={ogImagePreview}
                        onRemoveFavicon={() => {
                          setFaviconFile(null);
                          setFaviconPreview('');
                          handleInputChange('favicon', '');
                        }}
                        onRemoveOgImage={() => {
                          setOgImageFile(null);
                          setOgImagePreview('');
                          handleInputChange('ogImage', '');
                        }}
                      />
            
          )}
          
          {activeTab === 'integrations' && (
            <IntegrationsSection
              formData={formData}
              onInputChange={handleInputChange}
            />
          )}
          
          {activeTab === 'interactions' && (
            <InteractionsSection
              formData={formData}
              onInputChange={handleInputChange}
            />
          )}
          
          {activeTab === 'payments' && formData.isPremium && (
            <PaymentsSection
              formData={formData}
              onInputChange={handleInputChange}
              templateType="course"
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : <><Save className="h-4 w-4" /> Save Settings</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// Supporting Tab Components
const CourseBasicInfo = ({ formData, onInputChange, bookId, uploadStates, onLogoUpload }) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium mb-2">Course Title</label>
      <input
        type="text"
        value={formData.botName}
        onChange={(e) => onInputChange('botName', e.target.value)}
        className="w-full px-4 py-2 border rounded-lg"
        placeholder="Introduction to Web Development"
      />
    </div>
    
    <div>
      <label className="block text-sm font-medium mb-2">Description</label>
      <textarea
        value={formData.description}
        onChange={(e) => onInputChange('description', e.target.value)}
        className="w-full px-4 py-2 border rounded-lg h-32"
        placeholder="Course description..."
      />
    </div>
    
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium mb-2">Course Type</label>
        <select
          value={formData.courseType}
          onChange={(e) => onInputChange('courseType', e.target.value)}
          className="w-full px-4 py-2 border rounded-lg"
        >
          <option value="self-paced">Self-Paced</option>
          <option value="instructor-led">Instructor-Led</option>
          <option value="hybrid">Hybrid</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">Level</label>
        <select
          value={formData.level}
          onChange={(e) => onInputChange('level', e.target.value)}
          className="w-full px-4 py-2 border rounded-lg"
        >
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium mb-2">Course Logo</label>
      <FileUpload
        onFileSelect={onLogoUpload}
        currentFile={formData.logoUrl}
        accept="image/*"
        maxSize={5}
        label=""
        uploading={uploadStates.logo?.uploading}
        error={uploadStates.logo?.error}
        showPreview={true}
      />
      {formData.logoUrl && (
        <img 
          src={formData.logoUrl} 
          alt="Logo preview" 
          className="mt-2 h-20 w-20 object-contain border rounded"
        />
      )}
    </div>
  </div>
);

const CourseStructure = ({ formData, onInputChange }) => (
  <div className="space-y-6">
    <div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.enableModules}
          onChange={(e) => onInputChange('enableModules', e.target.checked)}
          className="rounded"
        />
        <span>Enable Modules (Group lessons into modules)</span>
      </label>
    </div>
    
    <div>
      <label className="block text-sm font-medium mb-2">Lesson Structure</label>
      <select
        value={formData.lessonStructure}
        onChange={(e) => onInputChange('lessonStructure', e.target.value)}
        className="w-full px-4 py-2 border rounded-lg"
      >
        <option value="linear">Linear (Complete in order)</option>
        <option value="flexible">Flexible (Any order)</option>
      </select>
    </div>
    
    <div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.enablePreview}
          onChange={(e) => onInputChange('enablePreview', e.target.checked)}
          className="rounded"
        />
        <span>Allow Preview Lessons</span>
      </label>
    </div>
  </div>
);

const InstructorSettings = ({ formData, onInputChange }) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium mb-2">Instructor Name</label>
      <input
        type="text"
        value={formData.instructorInfo?.name || ''}
        onChange={(e) => onInputChange('instructorInfo.name', e.target.value)}
        className="w-full px-4 py-2 border rounded-lg"
      />
    </div>
    
    <div>
      <label className="block text-sm font-medium mb-2">Instructor Bio</label>
      <textarea
        value={formData.instructorInfo?.bio || ''}
        onChange={(e) => onInputChange('instructorInfo.bio', e.target.value)}
        className="w-full px-4 py-2 border rounded-lg h-32"
      />
    </div>
  </div>
);

const CompletionSettings = ({ formData, onInputChange }) => (
  <div className="space-y-6">
    <div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.completionCriteria?.requireAllLessons}
          onChange={(e) => onInputChange('completionCriteria.requireAllLessons', e.target.checked)}
          className="rounded"
        />
        <span>Require All Lessons</span>
      </label>
    </div>
    
    <div>
      <label className="block text-sm font-medium mb-2">Minimum Score (%)</label>
      <input
        type="number"
        value={formData.completionCriteria?.minimumScore || 0}
        onChange={(e) => onInputChange('completionCriteria.minimumScore', parseInt(e.target.value))}
        className="w-full px-4 py-2 border rounded-lg"
        min="0"
        max="100"
      />
    </div>
    
    <div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.completionCriteria?.issuesCertificate}
          onChange={(e) => onInputChange('completionCriteria.issuesCertificate', e.target.checked)}
          className="rounded"
        />
        <span>Issue Certificate on Completion</span>
      </label>
    </div>
  </div>
);